import { ProductRequest } from '@italist/grpc-proto/services/inventory_pb';
import isEmpty from 'lodash/isEmpty';
import { NextPage } from 'next';
import React from 'react';

import AppWrapper from 'app_wrapper';
import Departments from 'models/departments';
import { ProductDetails as ProductDetailsComponent } from 'components/pages/ProductDetails/ProductDetails';
import { ShareButtonsConfig } from 'components/pages/ProductDetails/types/ShareButtonsConfig';
import { PageSpecificMetadataProps } from 'components/PageSpecificMetadata/PageSpecificMetadata';
import metadataValues from 'helpers/metadataValues';
import { getSocialTitle } from 'helpers/metadataValues/productDetail';
import { search } from 'helpers/routes';
import { Product } from 'models/product';
import { decorateProduct, getProductFilteredCategories } from 'shared/helpers/product';
import { decorateSearchResult, getRelatedProducts } from 'shared/helpers/search';
import { buildProductUrl } from '../shared/helpers/url';

const getAdrollProductGroup = country => {
  if (country.billing_currency_iso === 'USD') {
    return 'us';
  }
  if (country.is_tax_ue_zone === 'Y') {
    return 'eu';
  }
  return 'rest';
};

const ProductDetails: NextPage<{
  productDetails?: { product: Product };
  shareButtonsConfig?: ShareButtonsConfig;
}> = props => {
  const { productDetails } = props;
  const { product } = productDetails || {};

  const metadata: PageSpecificMetadataProps['metadata'] = product
    ? {
        description: metadataValues.productDetail.descriptionFactory(product),
        socialMetadataComp: metadataValues.productDetail.socialMetadataFactory({ product }),
        title: metadataValues.productDetail.titleFactory(product),
      }
    : null;

  const productDetailsNode = <ProductDetailsComponent {...props} />;

  return <AppWrapper desktopComp={productDetailsNode} metadata={metadata} mobileComp={productDetailsNode} />;
};

const getProduct = async (pvid, countryCode, billingCurrency, alternativeCurrencies) => {
  const inventoryClient = (await import('../shared/services/inventory')).default;
  const altCurrencies = [...alternativeCurrencies];
  // Klaviyo tracking uses prices in USD
  if (altCurrencies.indexOf('USD') < 0) {
    altCurrencies.push('USD');
  }
  const product = await new Promise<Product>((resolve, reject) => {
    const productReq = new ProductRequest();
    productReq.setProductVersionId(pvid);
    productReq.setCountryCode(countryCode);
    productReq.setBillingCurrency(billingCurrency);
    productReq.setAlternativeCurrenciesList(altCurrencies);
    productReq.setIsAvailableOnly(false);
    inventoryClient.getProduct(productReq, (err, rawprod) => {
      if (err) {
        reject(err);
        return;
      }
      resolve(decorateProduct(rawprod));
    });
  });
  return product;
};

ProductDetails.getInitialProps = async ({ req: _req, res: _res }) => {
  const quickauth = await import('../server/lib/quickauth');
  const countryHelper = (await import('../server/helpers/country')).default(quickauth);
  const req: any = _req;
  const res: { redirect: (string) => {} } = _res as any;
  const { departmentName: topLevelCategory, cat_3, brand_name, product_version_id: pvid } = req.params;
  const countryCode = req.session.mkt_country_code;
  const isCountryDDP = req.session.mkt_country_is_ddp;
  const billingCurrency = req.session.currency_code;
  const alternativeCurrencies = [];
  const country = countryHelper.getCountry(countryCode);
  if (req.session.approx_currency_code && req.session.approx_currency_code !== '') {
    alternativeCurrencies.push(req.session.approx_currency_code);
  }
  try {
    const product = await getProduct(pvid, countryCode, billingCurrency, alternativeCurrencies);

    req.datadog.increment('consumer_app.product_details');
    const url = req.url || 'none';
    const referrer = req.get('Referrer') || 'none';
    let noErrors = true;
    if (isEmpty(product) || !product.isActive || product.isExcluded) {
      req.datadog.increment('consumer_app.product_unavailable');
      req.datadog.event('Product unavailable requested', url, {}, { referrer });
      noErrors = false;
    }
    if (product && product.images && product.images.length === 0) {
      req.datadog.increment('consumer_app.product_no_images');
      req.datadog.event(
        'Product with no images requested',
        url,
        {},
        { product_id: product.productId, product_version_id: pvid, referrer },
      );
      noErrors = false;
    }

    const userFacingDepartment = Departments.getUserFacingDepartment(
      topLevelCategory.toLowerCase(),
      product.departmentsList.length > 0 ? product.departmentsList[0].internalName : null,
    );
    const overridingCanonicalUrl =
      product.departmentsList.length > 0 && Departments.isUnisexDepartment(product.departmentsList[0].internalName)
        ? buildProductUrl(product)
        : null;

    const departmentId = Departments.getDepartmentId(userFacingDepartment);
    if (!departmentId || getProductFilteredCategories(product, userFacingDepartment).length === 0) {
      (res as any).redirect(301, buildProductUrl(product));
      return {};
    }
    const searchResponse = await getRelatedProducts(
      countryCode,
      billingCurrency,
      alternativeCurrencies,
      product.brand.id,
      departmentId,
    );
    const relatedProducts = searchResponse
      .toObject()
      .productsList.map(relatedProduct => decorateSearchResult(relatedProduct, userFacingDepartment));

    const shareDescription = getSocialTitle(product);
    if (noErrors) {
      req.datadog.increment('pageview.product_details');
    }

    return {
      overridingCanonicalUrl,
      productDetails: {
        adrollCustomData: {
          product_group: getAdrollProductGroup(country),
        },
        canEdit: quickauth.hasStoreAdminAccess(req.session, product.store.storeId),
        exclusions: product.excludedCountriesList,
        isCountryDDP,
        product,
        relatedProducts,
        userFacingDepartment,
      },
      shareButtonsConfig: {
        pinterest: {
          description: shareDescription,
        },
        twitter: {
          description: shareDescription.replace('|', '%7C'),
        },
      },
    };
  } catch (err) {
    console.log(err);
    const department = Departments.getNonUnisexDepartment(topLevelCategory.toLowerCase());
    const highestLevelDepartment = Departments.getKidsDepartmentsInternalNames().includes(department)
      ? Departments.getKidsInternalName()
      : department;

    const productCategory = cat_3 || '';
    const brand = brand_name.replace(/-/g, ' ');
    let query;
    if (productCategory) {
      query = `${brand} ${productCategory}`;
    } else {
      query = brand;
    }

    const prefix = req.params && req.params.countryCode ? `/${req.params.countryCode}` : '';
    const pathname = `${prefix}${search(highestLevelDepartment, query)}`;
    req.datadog.increment('pageview.product_details_error');
    res.redirect(pathname);
    return {};
  }
};

export default ProductDetails;
